import React, { useMemo, useState } from "react";
import { FolderOpen, Upload, X } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useDocuments } from "../../../hooks/useDocuments";
import { isSystemAdmin } from "../../../constants/systemRoles";

const LEGACY_RAG_CONTROLS_ENABLED =
  process.env.REACT_APP_LEGACY_RAG_CONTROLS_ENABLED === "true"; // rollback-only; new flow uses KB Intake

const normalize = (v) =>
  String(v || "")
    .toLowerCase()
    .trim();

const titleCase = (v) =>
  String(v || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isIndexingStatus = (s) => s === "pending" || s === "indexing";

const getRagStatusLabel = (status) => {
  const s = normalize(status);
  if (!s || s === "not_indexed") return "Not indexed";
  if (isIndexingStatus(s)) return "Indexing…";
  if (s === "indexed") return "Indexed";
  if (s === "error" || s === "failed") return "Error";
  return status || "Unknown";
};

const getRagStatusClass = (status) => {
  const s = normalize(status);
  if (!s || s === "not_indexed") return "bg-gray-100 text-gray-700";
  if (isIndexingStatus(s)) return "bg-yellow-100 text-yellow-800";
  if (s === "indexed") return "bg-green-100 text-green-800";
  if (s === "error" || s === "failed") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-700";
};

const isPdfDoc = (d) => {
  const url = (d.file_url || "").toLowerCase();
  const name = (d.document_name || "").toLowerCase();
  const path = (d.file_path || "").toLowerCase();
  return url.endsWith(".pdf") || name.endsWith(".pdf") || path.endsWith(".pdf");
};

const fileNameWithoutExtension = (name) => {
  const clean =
    String(name || "document")
      .split(/[\\/]/)
      .pop() || "document";
  return clean.replace(/\.[^/.]+$/, "") || clean || "document";
};

const getDocumentBucket = (doc) => {
  const type = normalize(doc.document_type);
  const category = normalize(doc.category);

  if (
    ["rfi", "internal_rfi"].includes(type) ||
    ["rfi", "internal_rfi"].includes(category)
  ) {
    return "rfi";
  }

  if (
    ["submittal", "internal_submittal"].includes(type) ||
    ["submittal", "internal_submittal"].includes(category)
  ) {
    return "submittal";
  }

  return "documents";
};

const TAB_CONFIG = [
  {
    id: "documents",
    label: "Project Documents",
    description:
      "Drawings, specifications, reports, and general project files.",
    fixedType: null,
    fixedCategory: null,
    uploadButton: "Upload Documents",
  },
  {
    id: "rfi",
    label: "Uploaded RFIs",
    description:
      "RFI document uploads only. These files do not appear in Project Documents.",
    fixedType: "rfi",
    fixedCategory: "rfi",
    uploadButton: "Upload RFI Documents",
  },
  {
    id: "submittal",
    label: "Uploaded Submittals",
    description:
      "Submittal document uploads only. These files do not appear in Project Documents.",
    fixedType: "submittal",
    fixedCategory: "submittal",
    uploadButton: "Upload Submittal Documents",
  },
];

const sortByNewest = (a, b) => {
  const da = a.upload_date ? new Date(a.upload_date).getTime() : 0;
  const db = b.upload_date ? new Date(b.upload_date).getTime() : 0;
  if (db !== da) return db - da;
  return (b.id || 0) - (a.id || 0);
};

const getUniqueOptions = (docs, field) =>
  Array.from(
    new Set(
      (docs || []).map((d) => String(d?.[field] || "").trim()).filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

const SearchAndFilters = ({
  search,
  setSearch,
  ragFilter,
  setRagFilter,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  categoryFilter,
  setCategoryFilter,
  typeOptions,
  categoryOptions,
  statusOptions,
  showTypeCategoryFilters,
  showManagementFilters,
}) => {
  return (
    <div className="bg-white rounded-lg shadow px-3 py-3">
      <div className="flex flex-col lg:flex-row gap-2">
        <input
          className="border rounded-md px-3 py-2 text-sm flex-1 min-w-[220px]"
          placeholder="Search files..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {showTypeCategoryFilters && (
          <>
            <select
              className="border rounded-md px-3 py-2 text-sm lg:w-40"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All types</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {titleCase(type)}
                </option>
              ))}
            </select>

            <select
              className="border rounded-md px-3 py-2 text-sm lg:w-44"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All categories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {titleCase(cat)}
                </option>
              ))}
            </select>
          </>
        )}

        {showManagementFilters && (
          <>
            {/* <select
              className="border rounded-md px-3 py-2 text-sm lg:w-44"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </select> */}

            <select
              className="border rounded-md px-3 py-2 text-sm lg:w-44"
              value={ragFilter}
              onChange={(e) => setRagFilter(e.target.value)}
            >
              <option value="all">All RAG</option>
              <option value="not_indexed">Not indexed</option>
              <option value="indexing">Indexing</option>
              <option value="indexed">Indexed</option>
              <option value="error">Error</option>
            </select>
          </>
        )}
      </div>
    </div>
  );
};

const DocumentControlTable = ({
  title,
  docs,
  admin,
  canManageDocuments,
  indexingId,
  onOpenSemantic,
  onIndexForRag,
  onDelete,
}) => {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-3 py-2 border-b flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-gray-800">{title}</div>
        <div className="text-xs text-gray-500">
          {docs.length} file{docs.length === 1 ? "" : "s"}
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">No files found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Inspection</th>
                <th className="text-left px-3 py-2">Version</th>
                {/* {canManageDocuments && (
                  <th className="text-left px-3 py-2">Status</th>
                )} */}
                {canManageDocuments && LEGACY_RAG_CONTROLS_ENABLED && (
                  <th className="text-left px-3 py-2">RAG Status</th>
                )}
                <th className="text-left px-3 py-2">Link</th>
                {canManageDocuments && (
                  <th className="text-left px-3 py-2">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const rag = normalize(d.rag_index_status);
                const isIndexed = rag === "indexed";
                const isBusy = indexingId === d.id || isIndexingStatus(rag);
                const ragSupported = isPdfDoc(d);
                const semanticDisabled =
                  !admin || !ragSupported || isBusy || isIndexed;
                const basicDisabled =
                  !admin || !ragSupported || isBusy || isIndexed;

                return (
                  <tr
                    key={d.id}
                    className="border-t align-top hover:bg-gray-50/50"
                  >
                    <td className="px-3 py-2 min-w-[220px]">
                      <div className="font-medium text-gray-900">
                        {d.document_name || `Document #${d.id}`}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {d.file_size
                          ? `${Math.round(d.file_size / 1024)} KB`
                          : ""}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-gray-700">
                      {titleCase(d.document_type) || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {titleCase(d.category) || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {Array.isArray(d.inspection_numbers) &&
                      d.inspection_numbers.length
                        ? d.inspection_numbers.join(", ")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {d.version || "—"}
                    </td>

                    {/* {canManageDocuments && (
                      <td className="px-3 py-2 text-gray-700">
                        {titleCase(d.status) || "—"}
                      </td>
                    )} */}

                    {canManageDocuments && LEGACY_RAG_CONTROLS_ENABLED && (
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRagStatusClass(
                            d.rag_index_status,
                          )}`}
                          title={
                            d.rag_chunks_indexed != null
                              ? `Chunks indexed: ${d.rag_chunks_indexed}`
                              : ""
                          }
                        >
                          {getRagStatusLabel(d.rag_index_status)}
                        </span>
                        {!ragSupported && (
                          <div className="text-[11px] text-gray-400 mt-1">
                            PDF only
                          </div>
                        )}
                      </td>
                    )}

                    <td className="px-3 py-2">
                      {d.file_url ? (
                        <a
                          className="text-indigo-600 hover:underline text-xs"
                          href={d.file_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>

                    {canManageDocuments && (
                      <td className="px-3 py-2 min-w-[260px]">
                        <div className="flex flex-wrap items-center gap-2">
                          {LEGACY_RAG_CONTROLS_ENABLED && (
                            <>
                          <button
                            type="button"
                            onClick={() => {
                              if (!semanticDisabled) onOpenSemantic(d.id);
                            }}
                            disabled={semanticDisabled}
                            className="text-xs px-2.5 py-1 rounded-md border border-indigo-500 text-indigo-600 hover:bg-indigo-50 disabled:opacity-60"
                            title={
                              !admin
                                ? "Admins only"
                                : !ragSupported
                                  ? "PDF documents only"
                                  : isIndexed
                                    ? "Already indexed"
                                    : isBusy
                                      ? "Indexing in progress"
                                      : ""
                            }
                          >
                            Semantic Index
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (!basicDisabled) onIndexForRag(d.id);
                            }}
                            disabled={basicDisabled}
                            className="text-xs px-2.5 py-1 rounded-md border border-purple-500 text-purple-600 hover:bg-purple-50 disabled:opacity-60"
                            title={
                              !admin
                                ? "Admins only"
                                : !ragSupported
                                  ? "PDF documents only"
                                  : isIndexed
                                    ? "Already indexed"
                                    : isBusy
                                      ? "Indexing in progress"
                                      : ""
                            }
                          >
                            {indexingId === d.id
                              ? "Indexing..."
                              : "Index for RAG"}
                          </button>

                            </>
                          )}

                          <button
                            type="button"
                            onClick={() => onDelete(d)}
                            className="text-red-600 underline text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DocumentsView = ({ initialTab = "documents", lockedToTab = false }) => {
  const { activeProject, currentUser, projectRole } = useProjectContext();
  const admin = isSystemAdmin(currentUser?.role);
  const isExternalUser = normalize(currentUser?.role) === "user_external";
  const isProjectEngineer = normalize(projectRole) === "project_engineer";
  const canUploadDocuments = !isExternalUser && !isProjectEngineer;
  const canManageDocuments = !isExternalUser && !isProjectEngineer;

  const {
    documents = [],
    loading,
    uploading,
    uploadProgress,
    error,
    upload,
    remove,
    indexForRag,
    indexForRagSemantic,
  } = useDocuments();

  const safeInitialTab = TAB_CONFIG.some((t) => t.id === initialTab)
    ? initialTab
    : "documents";

  const [activeDocTab, setActiveDocTab] = useState(safeInitialTab);
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({
    document_name: "",
    document_type: "",
    category: "",
    version: "",
    status: "draft",
  });

  const [search, setSearch] = useState("");
  const [ragFilter, setRagFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [indexingId, setIndexingId] = useState(null);
  const [indexError, setIndexError] = useState(null);

  const [semanticOpen, setSemanticOpen] = useState(false);
  const [semanticDocId, setSemanticDocId] = useState(null);
  const [parsedTxt, setParsedTxt] = useState(null);
  const [maxLines, setMaxLines] = useState(20);
  const [outlineGroups, setOutlineGroups] = useState([
    { sectionCode: "", sectionTitle: "", partLabelsText: "" },
  ]);
  const [outlineJsonFile, setOutlineJsonFile] = useState(null);

  const activeConfig =
    TAB_CONFIG.find((t) => t.id === activeDocTab) || TAB_CONFIG[0];
  const visibleTabs = lockedToTab
    ? TAB_CONFIG.filter((t) => t.id === activeDocTab)
    : TAB_CONFIG;

  const bucketedDocs = useMemo(() => {
    const buckets = { documents: [], rfi: [], submittal: [] };
    (documents || []).forEach((doc) => {
      buckets[getDocumentBucket(doc)].push(doc);
    });
    Object.keys(buckets).forEach((key) => buckets[key].sort(sortByNewest));
    return buckets;
  }, [documents]);

  const currentDocs = bucketedDocs[activeDocTab] || [];

  const typeOptions = useMemo(
    () => getUniqueOptions(currentDocs, "document_type"),
    [currentDocs],
  );
  const categoryOptions = useMemo(
    () => getUniqueOptions(currentDocs, "category"),
    [currentDocs],
  );
  const statusOptions = useMemo(
    () => getUniqueOptions(currentDocs, "status"),
    [currentDocs],
  );

  const filteredDocs = useMemo(() => {
    const q = normalize(search);
    return currentDocs.filter((doc) => {
      const rag = normalize(doc.rag_index_status) || "not_indexed";
      const status = normalize(doc.status);
      const type = normalize(doc.document_type);
      const category = normalize(doc.category);

      if (ragFilter !== "all") {
        if (ragFilter === "error") {
          if (!["error", "failed"].includes(rag)) return false;
        } else if (ragFilter === "indexing") {
          if (!isIndexingStatus(rag)) return false;
        } else if (rag !== ragFilter) {
          return false;
        }
      }

      if (statusFilter !== "all" && status !== normalize(statusFilter))
        return false;
      if (typeFilter !== "all" && type !== normalize(typeFilter)) return false;
      if (categoryFilter !== "all" && category !== normalize(categoryFilter))
        return false;

      if (!q) return true;
      const haystack = [
        doc.document_name,
        doc.document_type,
        doc.category,
        ...(Array.isArray(doc.inspection_numbers)
          ? doc.inspection_numbers
          : []),
        doc.version,
        doc.status,
        doc.uploaded_by,
      ]
        .map((x) => normalize(x))
        .join(" ");

      return haystack.includes(q);
    });
  }, [
    currentDocs,
    search,
    ragFilter,
    statusFilter,
    typeFilter,
    categoryFilter,
  ]);

  if (!activeProject) return null;

  const resetUploadForm = () => {
    setFiles([]);
    setMeta({
      document_name: "",
      document_type: "",
      category: "",
      version: "",
      status: "draft",
    });
  };

  const resetFilters = () => {
    setSearch("");
    setRagFilter("all");
    setStatusFilter("all");
    setTypeFilter("all");
    setCategoryFilter("all");
  };

  const onChangeTab = (tabId) => {
    setActiveDocTab(tabId);
    resetUploadForm();
    resetFilters();
  };

  const closeSemantic = () => {
    setSemanticOpen(false);
    setSemanticDocId(null);
    setParsedTxt(null);
    setOutlineJsonFile(null);
    setMaxLines(20);
    setOutlineGroups([
      { sectionCode: "", sectionTitle: "", partLabelsText: "" },
    ]);
  };

  const addGroup = () =>
    setOutlineGroups((prev) => [
      ...prev,
      { sectionCode: "", sectionTitle: "", partLabelsText: "" },
    ]);

  const removeGroup = (idx) =>
    setOutlineGroups((prev) => prev.filter((_, i) => i !== idx));

  const updateGroup = (idx, patch) =>
    setOutlineGroups((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    );

  const buildOutlinePayloadV2 = () =>
    outlineGroups
      .map((g) => {
        const section_code = (g.sectionCode || "").trim();
        if (!section_code) return null;

        const section_title_raw = (g.sectionTitle || "").trim();
        const section_title = section_title_raw ? section_title_raw : null;

        const parts = (g.partLabelsText || "")
          .split("\n")
          .map((x) => x.trim())
          .filter(Boolean)
          .map((part_label) => ({ part_label }));

        return { section_code, section_title, parts };
      })
      .filter(Boolean);

  const loadOutlineFromJsonFile = async (file) => {
    const text = await file.text();
    const parsed = JSON.parse(text);

    if (!Array.isArray(parsed)) {
      throw new Error(
        "Outline JSON must be an array (list of section objects).",
      );
    }

    const groups = parsed.map((s) => ({
      sectionCode: String(s?.section_code || "").trim(),
      sectionTitle:
        s?.section_title == null ? "" : String(s.section_title).trim(),
      partLabelsText: Array.isArray(s?.parts)
        ? s.parts
            .map((p) => String(p?.part_label || "").trim())
            .filter(Boolean)
            .join("\n")
        : "",
    }));

    setOutlineGroups(
      groups.length
        ? groups
        : [{ sectionCode: "", sectionTitle: "", partLabelsText: "" }],
    );
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!canUploadDocuments || !files.length) return;

    try {
      setIndexError(null);

      for (const selectedFile of files) {
        const fallbackName = fileNameWithoutExtension(selectedFile.name);
        const useManualName =
          activeDocTab === "documents" &&
          files.length === 1 &&
          meta.document_name.trim();

        const uploadMetadata = {
          document_name: useManualName ? meta.document_name.trim() : fallbackName,
          document_type:
            activeConfig.fixedType || meta.document_type || "document",
          category: activeConfig.fixedCategory || meta.category || "document",
          version: meta.version || "",
          status: activeDocTab === "documents" ? meta.status || "draft" : "final",
          rag_mode: "manual",
        };

        if (activeDocTab === "documents") {
          await upload(selectedFile, uploadMetadata);
        } else {
          // RFI/Submittal tabs intentionally retain the existing backend-proxy
          // upload path; this optimization is scoped to Project Documents only.
          const fd = new FormData();
          fd.append("file", selectedFile);
          fd.append("document_name", uploadMetadata.document_name);
          fd.append("document_type", uploadMetadata.document_type);
          fd.append("category", uploadMetadata.category);
          fd.append("version", uploadMetadata.version);
          fd.append("status", uploadMetadata.status);
          await upload(fd);
        }
      }

      resetUploadForm();
    } catch (e2) {
      const detail =
        e2?.response?.data?.detail ||
        e2?.message ||
        "Failed to upload document.";
      setIndexError(detail);
    }
  };

  const handleIndexForRag = async (docId) => {
    try {
      setIndexError(null);
      setIndexingId(docId);
      await indexForRag(docId, { full_reindex: true });
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to index document for RAG.";
      setIndexError(detail);
    } finally {
      setIndexingId(null);
    }
  };

  const openSemantic = (docId) => {
    setIndexError(null);
    setSemanticDocId(docId);
    setParsedTxt(null);
    setOutlineJsonFile(null);
    setMaxLines(20);
    setOutlineGroups([
      { sectionCode: "", sectionTitle: "", partLabelsText: "" },
    ]);
    setSemanticOpen(true);
  };

  const submitSemantic = async () => {
    if (!semanticDocId || !parsedTxt) return;

    try {
      setIndexError(null);
      setIndexingId(semanticDocId);

      const fd = new FormData();
      fd.append("parsed_text_file", parsedTxt);

      if (outlineJsonFile) {
        fd.append("outline_json_file", outlineJsonFile);
      } else {
        const outline = buildOutlinePayloadV2();
        if (!outline.length) {
          setIndexError("Please add at least one Section code.");
          return;
        }
        fd.append("outline_json", JSON.stringify(outline));
      }

      fd.append("max_lines_per_chunk", String(Number(maxLines) || 20));

      await indexForRagSemantic(semanticDocId, fd);
      closeSemantic();
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to semantic-index document for RAG.";
      setIndexError(detail);
    } finally {
      setIndexingId(null);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!doc?.id || !canManageDocuments) return;

    const name = doc.document_name || `Document #${doc.id}`;
    const ok = window.confirm(
      `Delete "${name}"? This will remove the file from Document Control.`,
    );
    if (!ok) return;

    await remove(doc.id);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow px-3 py-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="w-5 h-5 text-purple-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-800 leading-tight">
                Document Control
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {activeProject.name} · documents, RFIs, and submittals
              </p>
              {!LEGACY_RAG_CONTROLS_ENABLED && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Requirement KB classification and indexing are managed from KB Intake.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => {
              const isActive = activeDocTab === tab.id;
              const count = bucketedDocs[tab.id]?.length || 0;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => !lockedToTab && onChangeTab(tab.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  } ${lockedToTab ? "cursor-default" : ""}`}
                >
                  {tab.label}
                  <span
                    className={`ml-2 inline-flex rounded-full px-1.5 py-0.5 text-[11px] ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-white text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {canUploadDocuments && (
        <form
          onSubmit={onUpload}
          className="bg-white rounded-lg shadow px-3 py-3"
        >
          <div className="flex flex-col xl:flex-row xl:items-end gap-2">
            {activeDocTab === "documents" ? (
              <>
                <input
                  className="border rounded-md px-3 py-2 text-sm xl:w-56"
                  placeholder="Name (optional)"
                  value={meta.document_name}
                  onChange={(e) =>
                    setMeta((p) => ({ ...p, document_name: e.target.value }))
                  }
                />
                <input
                  className="border rounded-md px-3 py-2 text-sm xl:w-40"
                  placeholder="Type"
                  value={meta.document_type}
                  onChange={(e) =>
                    setMeta((p) => ({ ...p, document_type: e.target.value }))
                  }
                />
                <input
                  className="border rounded-md px-3 py-2 text-sm xl:w-44"
                  placeholder="Category"
                  value={meta.category}
                  onChange={(e) =>
                    setMeta((p) => ({ ...p, category: e.target.value }))
                  }
                />
              </>
            ) : (
              <div className="rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-700 xl:w-56">
                {activeConfig.label.replace("Uploaded ", "")} upload
              </div>
            )}

            <input
              className="border rounded-md px-3 py-2 text-sm xl:w-32"
              placeholder="Version"
              value={meta.version}
              onChange={(e) =>
                setMeta((p) => ({ ...p, version: e.target.value }))
              }
            />

            <div className="flex-1 min-w-[220px]">
              <input
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm"
              />
              <div className="text-[11px] text-gray-500 mt-1 truncate">
                {files.length
                  ? `${files.length} file${files.length === 1 ? "" : "s"} selected · names saved without extensions`
                  : "Select one or more files"}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetUploadForm}
                className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50"
                disabled={uploading}
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={uploading || !files.length}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60"
              >
                <Upload className="w-4 h-4" />
                {uploading ? (activeDocTab === "documents" && uploadProgress != null ? `Uploading… ${uploadProgress}%` : "Uploading...") : "Upload"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* {!canUploadDocuments && (
        <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-lg px-3 py-2 text-xs"></div>
      )} */}

      {(error || indexError) && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
          {error && <div>Failed to load/save documents.</div>}
          {indexError && <div className="mt-1">{indexError}</div>}
        </div>
      )}

      <SearchAndFilters
        search={search}
        setSearch={setSearch}
        ragFilter={ragFilter}
        setRagFilter={setRagFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        typeOptions={typeOptions}
        categoryOptions={categoryOptions}
        statusOptions={statusOptions}
        showTypeCategoryFilters={activeDocTab === "documents"}
        showManagementFilters={canManageDocuments && LEGACY_RAG_CONTROLS_ENABLED}
      />

      {loading && (
        <div className="p-4 text-sm text-gray-500 bg-white rounded-lg shadow">
          Loading...
        </div>
      )}

      {!loading && !error && (
        <>
          {semanticOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/30"
                onClick={closeSemantic}
              />
              <div className="relative bg-white w-full max-w-3xl rounded-lg shadow p-6 max-h-[85vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-800">
                      Semantic Index
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Upload the parsed TXT and define your outline as dynamic
                      section blocks.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={closeSemantic}
                    className="p-2 rounded-md hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    Parsed text file (.txt)
                  </div>
                  <input
                    type="file"
                    accept=".txt,.md"
                    onChange={(e) => setParsedTxt(e.target.files?.[0] || null)}
                  />
                  {!parsedTxt && (
                    <div className="text-xs text-gray-500 mt-1">
                      Required for semantic indexing.
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    Outline JSON file (.json) (optional)
                  </div>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null;
                      setOutlineJsonFile(f);
                      if (!f) return;

                      try {
                        setIndexError(null);
                        await loadOutlineFromJsonFile(f);
                      } catch (err) {
                        setIndexError(err?.message || "Invalid JSON file.");
                      }
                    }}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    If you upload a JSON outline, it will be used for indexing
                    and will auto-fill the fields.
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  {outlineGroups.map((g, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-800">
                          Section #{idx + 1}
                        </div>
                        {outlineGroups.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeGroup(idx)}
                            className="text-xs text-red-600 underline"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="mt-2">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          Section code (required)
                        </div>
                        <input
                          className="w-full border rounded-md px-2 py-2 text-sm"
                          placeholder="e.g. Part 2. Safety Rules for the Installation and Maintenance..."
                          value={g.sectionCode}
                          onChange={(e) =>
                            updateGroup(idx, { sectionCode: e.target.value })
                          }
                        />
                      </div>

                      <div className="mt-3">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          Section title (optional)
                        </div>
                        <input
                          className="w-full border rounded-md px-2 py-2 text-sm"
                          placeholder="e.g. Section 20. Purpose, scope, and application of rules"
                          value={g.sectionTitle}
                          onChange={(e) =>
                            updateGroup(idx, { sectionTitle: e.target.value })
                          }
                        />
                      </div>

                      <div className="mt-3">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          Part labels (one per line)
                        </div>
                        <textarea
                          className="w-full border rounded-md p-2 text-sm min-h-[120px]"
                          placeholder={`200. Purpose\n201. Scope\n202. Application of rules`}
                          value={g.partLabelsText}
                          onChange={(e) =>
                            updateGroup(idx, { partLabelsText: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addGroup}
                    className="text-sm px-3 py-2 rounded-md border border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                  >
                    + Add Section
                  </button>
                </div>

                <div className="flex items-center justify-between mt-5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      Lines per chunk
                    </span>
                    <input
                      type="number"
                      className="border rounded-md px-2 py-1 text-sm w-24"
                      value={maxLines}
                      min={5}
                      max={200}
                      onChange={(e) => setMaxLines(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-2 text-sm rounded-md border"
                      onClick={closeSemantic}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white disabled:opacity-60"
                      disabled={!parsedTxt || indexingId === semanticDocId}
                      onClick={submitSemantic}
                    >
                      {indexingId === semanticDocId ? "Indexing..." : "Index"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DocumentControlTable
            title={activeConfig.label}
            docs={filteredDocs}
            admin={admin}
            canManageDocuments={canManageDocuments}
            indexingId={indexingId}
            onOpenSemantic={openSemantic}
            onIndexForRag={handleIndexForRag}
            onDelete={handleDeleteDocument}
          />
        </>
      )}
    </div>
  );
};

export default DocumentsView;
